import type { Component } from 'vue';

export type RoutePermission = 'admin' | 'elqano' | 'elqano_admin';

export interface AppRouteMeta {
    public?: boolean;
    allowWithoutSubscription?: boolean;
    allowWithoutOnboarding?: boolean;
    requiredPermission?: RoutePermission;
    icon?: Component;
    showInSidebar?: boolean;
    showInUserMenu?: boolean;
}

declare module 'vue-router' {
    interface RouteMeta extends AppRouteMeta {}
}

type TenantPermissions = {
    tenantAdmin: boolean;
    tenantElqano: boolean;
};

type AccessEntitlements = {
    loaded: boolean;
    hasActiveSubscription: boolean;
    hasCompletedOnboarding: boolean;
};

type RouteLike = {
    meta?: AppRouteMeta;
};

export function isPublicRoute(route: RouteLike) {
    return route.meta?.public === true;
}

export function hasRequiredPermission(
    route: RouteLike,
    tenantPermissions: TenantPermissions
) {
    switch (route.meta?.requiredPermission) {
        case 'admin':
            return tenantPermissions.tenantAdmin;
        case 'elqano':
            return tenantPermissions.tenantElqano;
        case 'elqano_admin':
            return (
                tenantPermissions.tenantElqano &&
                tenantPermissions.tenantAdmin
            );
        default:
            return true;
    }
}

export function canAccessRoute(
    route: RouteLike,
    options: {
        isAuthenticated: boolean;
        tenantPermissions: TenantPermissions;
        accessEntitlements: AccessEntitlements;
    }
) {
    if (isPublicRoute(route)) {
        return true;
    }

    if (!options.isAuthenticated || !options.accessEntitlements.loaded) {
        return false;
    }

    if (
        !route.meta?.allowWithoutSubscription &&
        !options.accessEntitlements.hasActiveSubscription
    ) {
        return false;
    }

    if (
        !route.meta?.allowWithoutOnboarding &&
        !options.accessEntitlements.hasCompletedOnboarding
    ) {
        return false;
    }

    return hasRequiredPermission(route, options.tenantPermissions);
}
